import React from "react";
import not from "ramda/src/not";

import { IGender } from "../../services/NetworkApi";

import { useSnackbar } from "../../hooks/useSnackbar";
import { useNetwork } from "../../hooks/useNetwork";
import { useNetworkContext } from "../../contexts/NetworkContext/context";
import useAuth from "../../routes/login/AuthContext";
import Api from "../../services/Api";
import { TableRowOptions } from "../TableRowOptions";
import { TableRowEditOptions } from "../TableRowEditOptions";
import { useModalContext } from "../../contexts/ModalContext/context";
import { GenderDeleteModal } from "./GenderDeleteModal";
import { ReadOnlyWrapper } from "../ReadOnlyWrapper";
import { TableRow } from "../../UI/TableRow";
import { TableCell } from "../../UI/TableCell";
import { TextField } from "../../UI/TextField";
import { GenderGroupSelect } from "./GenderGroupSelect";

interface IProps extends IGender {
  subnetwork: number;
  isDisabled?: boolean;
}

export const GenderOptionsTableRow: React.FC<IProps> = (props) => {
  const [isEdit, setIsEdit] = React.useState(false);
  const { setIsOpen } = useModalContext();
  const [name, setName] = React.useState(props.name);
  const [isEditLoading, setIsEditLoading] = React.useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = React.useState(false);
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const { selectedNetwork } = useNetwork();
  const { loadNetworkInfoForNetworks } = useNetworkContext();
  const { getAuthToken } = useAuth();

  const toggleEdit = () => setIsEdit(not);
  const toggleDeleteModal = () => setIsOpen(not);

  const handlChangeByKey = (key: "groupId" | "genderName") => async (
    value: number | string
  ) => {
    const res = await Api.setGender(
      selectedNetwork.l1Id,
      {
        l1Id: selectedNetwork.l1Id,
        l2Id: props.subnetwork,
        id: props.id,
        [key]: value,
      },
      await getAuthToken()
    );

    if (res.success) {
      showSuccessSnackbar("Saved Changes");
    } else {
      showErrorSnackbar("An Error Occurred");
    }

    return loadNetworkInfoForNetworks([selectedNetwork.l1Id]);
  };

  const handlGenderDelete = async () => {
    toggleDeleteModal();
    setIsDeleteLoading(true);
    const res = await Api.deleteGender(
      selectedNetwork.l1Id,
      props.id,
      await getAuthToken()
    );

    if (res) {
      showSuccessSnackbar("Saved Changes");
    } else {
      showErrorSnackbar("An Error Occurred");
    }

    await loadNetworkInfoForNetworks([selectedNetwork.l1Id]);
    setIsDeleteLoading(false);
  };

  const handleNameChange = async (e: React.ChangeEvent<{ value: string }>) => {
    setName(e.target.value);
  };

  const handleEditConfirm = async () => {
    toggleEdit();
    setIsEditLoading(true);
    await handlChangeByKey("genderName")(name);
    setIsEditLoading(false);
  };

  return (
    <TableRow>
      <TableCell>
        {isEdit && <TextField value={name} onChange={handleNameChange} />}
        {!isEdit && props.name}
      </TableCell>
      <TableCell>
        <ReadOnlyWrapper>
          <GenderGroupSelect
            defaultValue={props.groupId}
            subnetwork={props.subnetwork}
            onChange={handlChangeByKey("groupId")}
            isDisabled={props.isDisabled}
          />
        </ReadOnlyWrapper>
      </TableCell>
      <TableCell>
        {isEdit && (
          <TableRowEditOptions
            handleCancel={toggleEdit}
            handleConfirm={handleEditConfirm}
          />
        )}
        {!isEdit && (
          <TableRowOptions
            handleEdit={toggleEdit}
            isEditLoading={isEditLoading}
            handleDelete={toggleDeleteModal}
            isDeleteLoading={isDeleteLoading}
            editDisabled={props.isDisabled}
          />
        )}
        <GenderDeleteModal
          gender={props.name}
          handleDelete={handlGenderDelete}
        />
      </TableCell>
    </TableRow>
  );
};
